import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MapPin, Clock, DollarSign, X, Loader, ChevronLeft, ChevronRight,
  CalendarDays, Search, Users, ImageIcon, Check, Phone,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../contexts/ThemeContext';
import { academyAPI } from '../../services/api';
import { useStripeReturn } from '../../payments/services/PaymentStripeHooks';

/**
 * Booking courts, as a player.
 *
 * TWO THINGS DECIDE THIS DESIGN.
 *
 * First, a day at a time is no way to find an hour. Loading one date, seeing
 * nothing and clicking the next is how you spend a minute discovering that
 * Thursday works. So the page asks for a WEEK and shows the whole of it at once:
 * days down, hours across, one screen, no hunting.
 *
 * Second, people do not book "an hour". They book a game — two hours of
 * doubles, three for a session with a coach — and an hour-by-hour list makes
 * them work out for themselves which free hours happen to sit next to each
 * other on the same court. So DURATION IS A FILTER, applied before anything is
 * drawn: pick 2 hours and every cell on the grid is a place a two-hour booking
 * can actually start, on one court, uninterrupted. Nothing shown is a slot you
 * then discover you cannot have.
 *
 * At thirty courts the cell says how many courts can give you that run, and the
 * court itself is the detail behind it — photos, surface, rate — because with
 * thirty of them the court is what you choose last, not first.
 */

const HOUR_LABEL = (d) => new Date(d).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const DAY_SHORT  = (d) => d.toLocaleDateString(undefined, { weekday: 'short' });
const DATE_SHORT = (d) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const sameDay = (a, b) => iso(a) === iso(b);

const DURATIONS = [1, 2, 3, 4];
const PARTS = [
  { id: 'any',       label: 'Any time',  test: () => true },
  { id: 'morning',   label: 'Morning',   test: (h) => h < 12 },
  { id: 'afternoon', label: 'Afternoon', test: (h) => h >= 12 && h < 17 },
  { id: 'evening',   label: 'Evening',   test: (h) => h >= 17 },
];

const money = (n) => (n == null ? 'Free' : `$${Number(n) % 1 === 0 ? Number(n) : Number(n).toFixed(2)}`);
const HOUR_MS = 3_600_000;

/**
 * Every place a run of `hours` consecutive free hours can start, on one court.
 *
 * Consecutive means touching in time as well as free: two open windows either
 * side of a closure both contain free hours, and a booking cannot span the gap.
 * Walking the slots and checking each one begins exactly where the last ended is
 * what stops the page offering a three-hour run that is really two plus one.
 */
function runsFor(court, hours) {
  const out = [];
  const slots = court.slots;
  for (let i = 0; i + hours <= slots.length; i += 1) {
    let ok = true;
    for (let k = 0; k < hours; k += 1) {
      const s = slots[i + k];
      if (!s.available) { ok = false; break; }
      if (k > 0 && new Date(s.start).getTime() !== new Date(slots[i + k - 1].end).getTime()) { ok = false; break; }
    }
    if (!ok) continue;
    const start = new Date(slots[i].start);
    const end   = new Date(slots[i + hours - 1].end);
    const price = slots.slice(i, i + hours)
      .reduce((sum, s) => (s.price == null ? sum : sum + Number(s.price)), 0);
    out.push({ court, start, end, price: price || null, hours });
  }
  return out;
}

/* ── Court detail ─────────────────────────────────────────────────────────── */

const CourtDetail = ({ court, onClose, theme }) => {
  const [shot, setShot] = useState(0);
  const images = court.imageKeys || [];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4" onClick={onClose}>
      <div className={`${theme.card} w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl`}
           onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          {images.length > 0 ? (
            <>
              <img src={`/images/${images[shot]}`} alt={court.name} className="w-full h-56 object-cover rounded-t-2xl" />
              {images.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((k, i) => (
                    <button key={k} onClick={() => setShot(i)} aria-label={`Photo ${i + 1}`}
                      className={`w-2 h-2 rounded-full ${i === shot ? 'bg-white' : 'bg-white/50'}`} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className={`w-full h-28 rounded-t-2xl ${theme.backgroundSecondary} flex flex-col items-center justify-center gap-1`}>
              <ImageIcon size={20} className={theme.textMuted} />
              <span className={`text-xs ${theme.textMuted}`}>No photos yet</span>
            </div>
          )}
          <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-black/40 text-white">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`text-xl font-bold ${theme.text}`}>{court.name}</h3>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${theme.tag}`}>
              {court.type === 'field' ? 'Field' : 'Court'}
            </span>
          </div>
          <div className={`flex items-center gap-3 text-sm ${theme.textSecondary}`}>
            {court.sports?.length > 0 && <span>{court.sports.join(' · ')}</span>}
            {court.capacity != null && <span className="flex items-center gap-1"><Users size={13} />{court.capacity}</span>}
            <span className="flex items-center gap-1 font-medium"><DollarSign size={13} />{money(court.hourlyRate)}/hour</span>
          </div>
          {court.description && <p className={`text-sm ${theme.textSecondary}`}>{court.description}</p>}
        </div>
      </div>
    </div>
  );
};

/* ── The page ─────────────────────────────────────────────────────────────── */

/**
 * One question and its answers, kept on a single line.
 *
 * The chips scroll sideways inside their own row rather than wrapping, so a
 * narrow screen shows a short filter bar you can flick through instead of a
 * four-line block that pushes the courts themselves below the fold. The
 * negative margin lets the first and last chip sit flush with the page edge
 * while still having something to scroll into.
 */
const FilterRow = ({ theme, label, children }) => (
  <div className="flex items-center gap-2 min-w-0">
    <span className={`text-sm font-medium ${theme.textSecondary} shrink-0`}>{label}</span>
    <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 py-0.5">
      {children}
    </div>
  </div>
);

const CourtsPublicTab = ({ academyId, academy = {} }) => {
  const { theme } = useTheme();

  const [weekStart, setWeekStart] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [hours, setHours]   = useState(1);
  const [sport, setSport]   = useState('all');
  const [part, setPart]     = useState('any');
  const [courts, setCourts] = useState([]);
  const [nextFree, setNextFree] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [cell, setCell]     = useState(null);     // {day, hour, options}
  const [detail, setDetail] = useState(null);
  // The caller's own court bookings at THIS academy. /bookings/me returns them
  // across every academy, so it is filtered here rather than adding a server
  // parameter for one screen.
  const [myBookings, setMyBookings] = useState([]);
  const [cancelling, setCancelling] = useState(null);   // booking id in flight
  const [busy, setBusy]     = useState(false);
  const [hunting, setHunting] = useState(false);
  // The order the shared checkout is settling, once the hour is held.
  const [checkoutOrderId, setCheckoutOrderId] = useState(null);
  // The request-a-booking flow is not built yet. Until it is, the button says
  // so and hands over the one thing that does work today — the phone.
  const [askOpen, setAskOpen] = useState(false);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }),
    [weekStart],
  );

  const load = useCallback(() => {
    setLoading(true);
    academyAPI.courts.myBookings()
      .then(r => setMyBookings(Array.isArray(r) ? r : (r?.bookings || [])))
      .catch(() => setMyBookings([]));   // a failure here must not hide the grid

    academyAPI.courts.dayAvailability(academyId, iso(days[0]), iso(days[6]))
      .then(r => { setCourts(r?.courts || []); setNextFree(r?.nextAvailable || null); })
      .catch(() => { setCourts([]); setNextFree(null); })
      .finally(() => setLoading(false));
  }, [academyId, days]);

  useEffect(load, [load]);

  /**
   * Coming back from Stripe having paid for an hour.
   *
   * Every other way of settling a court says so — the counter path toasts from
   * onComplete below, a free court toasts from confirm() — but the card path
   * leaves the page for Stripe, so onComplete never runs and this tab said
   * nothing at all. The page-level banner is not a substitute: it is worded for
   * a registration, and it does not reload this grid, so the hour that was just
   * paid for still drew as merely held.
   *
   * Mounting a second copy of the hook here is safe: it shares one verification
   * with the page's copy rather than racing it — see PaymentStripeHooks.
   */
  useStripeReturn({
    onPaid: () => {
      toast.success('Paid — the court is yours');
      load();
    },
  });

  // Back from Stripe without paying — by its back link or the device's own
  // gesture. Reopens checkout on the SAME order so they can try again or
  // switch method, rather than landing on the list with nothing said.
  const navigate = useNavigate();
  // No auto-redirect on load.
  //
  // A leftover from when checkout was a dialog: a note in sessionStorage sent
  // the payer to /checkout the moment this tab mounted. Once every flow raised
  // its own order the note went stale, and opening Courts jumped to a checkout
  // for something else entirely — a tournament entry, under a "Court hire"
  // heading. /checkout owns returning from Stripe now; nothing needs to chase
  // the payer there.

  const sports = useMemo(() => [...new Set(courts.flatMap(c => c.sports || []))].sort(), [courts]);
  const partTest = PARTS.find(p => p.id === part)?.test || (() => true);

  /** Every run of the wanted length, across the week, that passes the filters. */
  const runs = useMemo(() => {
    const out = [];
    for (const court of courts) {
      if (sport !== 'all' && !(court.sports || []).includes(sport)) continue;
      for (const r of runsFor(court, hours)) {
        if (!partTest(r.start.getHours())) continue;
        out.push(r);
      }
    }
    return out;
  }, [courts, sport, hours, part]); // eslint-disable-line react-hooks/exhaustive-deps

  // The grid: hour columns across, day rows down, each cell the runs that start
  // there. Columns come from the data, so an academy open 6–10 shows five
  // columns rather than a day's worth of empty ones.
  const hourCols = useMemo(() => {
    const set = new Set(runs.map(r => r.start.getHours()));
    return [...set].sort((a, b) => a - b);
  }, [runs]);

  const cellsByDay = useMemo(() => {
    const map = new Map();
    for (const r of runs) {
      const key = `${iso(r.start)}|${r.start.getHours()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return map;
  }, [runs]);

  const cheapest = (list) => list.reduce((a, b) => ((b.price ?? Infinity) < (a.price ?? Infinity) ? b : a));

  /**
   * Hold the hour, then go and pay for it.
   *
   * Called straight from Book. There used to be a confirmation dialog on top of
   * the slot list that restated the court, the day, the time and the price —
   * all four of which are already on the card the payer just pressed Book on —
   * and asked them to press Confirm to say yes to what they had already said
   * yes to. Checkout shows the same summary and is where the decision actually
   * costs something.
   */
  const confirm = async (pick) => {
    if (!pick) return;
    setBusy(true);
    const hhmm = (d) => `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
    try {
      const b = await academyAPI.courts.book(academyId, pick.court.id, {
        date: pick.start.toISOString().slice(0, 10),
        startTime: hhmm(pick.start),
        endTime: hhmm(pick.end),
      });
      setCell(null);

      // STRAIGHT INTO THE SAME CHECKOUT AS EVERYTHING ELSE.
      //
      // The hour is held first — that is what stops somebody else taking it
      // while you find your card — but a booking that ends there leaves the
      // player looking at a confirmation for something they have not paid for,
      // and the academy holding an unpaid hour it thinks is sold. Programme
      // registration and open play both hand off to /checkout here,
      // and a court is not different enough to deserve its own way out.
      if (b?.order_id) {
        // The checkout PAGE, for the same reason programmes and open play use
        // it: the hour is already held, so all that is left is paying for it,
        // and a dialog cannot survive being sent to stripe.com.
        const from = `${window.location.pathname}${window.location.search}`;
        // The hour is held before payment, so walking away from checkout has to
        // give it back — otherwise the court stays sold to somebody who decided
        // not to buy it, and the academy cannot tell the difference.
        const release = b?.id ? `&release=court:${academyId}:${pick.court.id}:${b.id}` : '';
        navigate(`/checkout?order=${b.order_id}`
          + `&from=${encodeURIComponent(from)}`
          + release
          + `&academy=${encodeURIComponent(academy?.slug || academy?.id || '')}`
          + `&title=${encodeURIComponent('Court hire')}`);
      } else {
        // Free court, or no rate set — nothing to settle.
        toast.success(`${pick.court.name} booked, ${HOUR_LABEL(pick.start)}–${HOUR_LABEL(pick.end)}`);
        load();
      }
    } catch (err) {
      toast.error(err?.message || 'Could not hold that');
    } finally { setBusy(false); }
  };

  const chip = (on) => `px-3 py-1.5 rounded-full text-sm font-medium border transition whitespace-nowrap ${
    on ? `${theme.tabActive} border-transparent` : `${theme.border} ${theme.textSecondary} ${theme.cardHover}`}`;

  const shiftWeek = (n) => setWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() + n * 7); return d; });

  /**
   * Walk forward until a week actually contains what was asked for.
   *
   * "Nothing this week" with only a next arrow underneath is the same dead end
   * as a day at a time, just a week wide: an academy whose Tuesdays are booked
   * solid for a fortnight makes somebody click eight times to learn it. This
   * asks each week in turn and stops at the first that has a run of the wanted
   * length — the answer to "when CAN I get three hours", which is the actual
   * question.
   */
  const findNext = async () => {
    setHunting(true);
    try {
      const cursor = new Date(weekStart);
      for (let week = 1; week <= 8; week += 1) {
        cursor.setDate(cursor.getDate() + 7);
        const end = new Date(cursor); end.setDate(end.getDate() + 6);
        const r = await academyAPI.courts.dayAvailability(academyId, iso(cursor), iso(end));
        const hit = (r?.courts || []).some(c => {
          if (sport !== 'all' && !(c.sports || []).includes(sport)) return false;
          return runsFor(c, hours).some(run => partTest(run.start.getHours()));
        });
        if (hit) { setWeekStart(new Date(cursor)); return; }
      }
      toast(`No ${hours}-hour slot in the next two months`, { icon: '🔍' });
    } catch {
      toast.error('Could not search ahead');
    } finally { setHunting(false); }
  };
  // Confirmed and still ahead of us. A cancelled booking or last month's game
  // is history, and this block is about what the player still has to turn up to.
  const mine = useMemo(() => {
    const now = Date.now();
    const nameOf = new Map((courts || []).map(c => [c.id, c.name]));
    return (myBookings || [])
      .filter(b => String(b.academy_id) === String(academyId))
      .filter(b => b.status === 'confirmed' && new Date(b.end_time).getTime() >= now)
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
      .map(b => ({ ...b, courtName: nameOf.get(b.court_id) || `Court ${b.court_id}` }));
  }, [myBookings, academyId, courts]);

  /**
   * Give up an hour, and say what became of the money.
   *
   * The server cancels and refunds in one call, through the same PaymentRefund
   * the other offerings use, and hands back `message` — the sentence written
   * for whatever actually happened: a card reversed, cash to collect at the
   * desk, or a refund Stripe refused, with the venue's number in it. It is
   * shown as given rather than re-decided here; a second opinion written on
   * this screen would be free to drift from the one the server acted on.
   *
   * Reloads afterwards so the freed hour reappears in the grid below, not just
   * out of this list — the slot is bookable again the moment it is released.
   */
  const releaseCourt = async (b) => {
    setCancelling(b.id);
    try {
      const res = await academyAPI.courts.cancelBooking(academyId, b.court_id, b.id);

      // A refund that did not go through is the one that has to be read, so it
      // is a warning that stays on screen rather than a success that slides by.
      if (res?.refund && res.refund.ok === false) {
        toast(`Booking cancelled. ${res.message}`, { icon: '⚠️', duration: 12000 });
      } else if (res?.message) {
        toast.success(`Booking cancelled. ${res.message}`, { duration: 8000 });
      } else {
        toast.success('Booking cancelled.');
      }
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not cancel that booking');
    } finally {
      setCancelling(null);
    }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  const fmtWhen = (b) => {
    const st = new Date(b.start_time), en = new Date(b.end_time);
    const day  = st.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    const time = (d) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${day} · ${time(st)} – ${time(en)}`;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
      {/* What the player already holds, before what they could book. Someone
          arriving at this tab usually wants to check a booking, not make one. */}
      {mine.length > 0 && (
        <div className={`rounded-xl border ${theme.border} ${theme.card} p-4`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={`text-base font-semibold ${theme.text}`}>Your court bookings</h3>
            <span className={`text-xs ${theme.textSecondary}`}>
              {mine.length} confirmed
            </span>
          </div>

          <ul className="space-y-2">
            {mine.map(b => (
              <li
                key={b.id}
                className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border ${theme.border} px-3 py-2`}
              >
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${theme.text}`}>{b.courtName}</p>
                  <p className={`text-xs ${theme.textSecondary}`}>{fmtWhen(b)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                    Confirmed
                  </span>
                  {/* Payment is a separate fact from the booking existing — an
                      unpaid one is still confirmed, and the player needs to know. */}
                  {b.payment_status !== 'paid' && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      Unpaid
                    </span>
                  )}
                  {b.order_number && (
                    <span className={`text-xs ${theme.textSecondary}`}>{b.order_number}</span>
                  )}
                  {/* One booking, one button. Acts on this hour alone and
                      reports what happened to its payment — there is no
                      selection to reconcile and nothing to submit. */}
                  <button
                    type="button"
                    onClick={() => releaseCourt(b)}
                    disabled={cancelling === b.id}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold border ${theme.border} ${
                      cancelling === b.id
                        ? 'opacity-50 cursor-not-allowed'
                        : 'text-pink-600 dark:text-pink-400 hover:bg-pink-50 dark:hover:bg-pink-900/20'
                    }`}
                  >
                    {cancelling === b.id ? 'Cancelling…' : 'Cancel'}
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {/* Requests are not built yet. Saying so beats an empty heading that
              looks broken, and beats silence when the button above offers it. */}
          <p className={`text-xs ${theme.textSecondary} mt-3 pt-3 border-t ${theme.border}`}>
            Requests you send with <strong className={theme.text}>Ask for a time</strong> will appear
            here once that is available.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className={`text-2xl font-bold ${theme.text}`}>Book a court</h2>
          <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
            {loading ? 'Looking…'
              : runs.length > 0
                ? <><strong className={theme.text}>{runs.length}</strong> {hours}-hour {runs.length === 1 ? 'slot' : 'slots'} this week</>
                : `No ${hours}-hour slot this week`}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {/* Also here, not only in the empty state: a grid with slots on it can
              still be missing the hour somebody actually wants, and that is
              exactly when they give up rather than ask. */}
          <button
            onClick={() => setAskOpen(true)}
            className={`mr-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border ${theme.border} ${theme.textSecondary} ${theme.cardHover} text-sm font-medium whitespace-nowrap`}
          >
            <Phone size={14} /> Ask for a time
          </button>
          <button onClick={() => shiftWeek(-1)} disabled={weekStart <= today}
            className={`p-2 rounded-lg border ${theme.border} ${theme.text} disabled:opacity-30`} aria-label="Previous week">
            <ChevronLeft size={15} />
          </button>
          <span className={`text-sm font-medium ${theme.text} px-2 whitespace-nowrap`}>
            {DATE_SHORT(days[0])} – {DATE_SHORT(days[6])}
          </span>
          <button onClick={() => shiftWeek(1)}
            className={`p-2 rounded-lg border ${theme.border} ${theme.text}`} aria-label="Next week">
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {/* How long, which sport, what time of day. Duration first, because it
          decides what counts as available at all.

          Three named rows rather than one wrapping line separated by pipes. On
          a desktop the old version was a single tidy row; on a phone it wrapped
          into four, and the pipes — which only ever meant "a different question
          starts here" — ended up stranded at the start and end of lines,
          separating nothing. A phone gets one scrollable row per question, a
          wide screen gets them side by side, and the label says what is being
          asked in both. */}
      <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-4">
        <FilterRow theme={theme} label="I want">
          {DURATIONS.map(h => (
            <button key={h} onClick={() => setHours(h)} className={chip(hours === h)}>
              {h} {h === 1 ? 'hour' : 'hours'}
            </button>
          ))}
        </FilterRow>

        {sports.length > 1 && (
          <FilterRow theme={theme} label="Sport">
            <button onClick={() => setSport('all')} className={chip(sport === 'all')}>All sports</button>
            {sports.map(s => <button key={s} onClick={() => setSport(s)} className={chip(sport === s)}>{s}</button>)}
          </FilterRow>
        )}

        <FilterRow theme={theme} label="When">
          {PARTS.map(p => <button key={p.id} onClick={() => setPart(p.id)} className={chip(part === p.id)}>{p.label}</button>)}
        </FilterRow>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader size={26} className={`animate-spin ${theme.link}`} /></div>
      ) : runs.length === 0 ? (
        <div className={`${theme.card} rounded-2xl text-center py-16`}>
          <Search className={`w-10 h-10 mx-auto mb-3 ${theme.textMuted}`} />
          <p className={`font-semibold ${theme.text}`}>
            Nothing with {hours} {hours === 1 ? 'hour' : 'hours'} free this week
          </p>
          <p className={`text-sm ${theme.textMuted} mt-1`}>
            Try a shorter booking, another sport, or search ahead.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
            <button
              onClick={() => setAskOpen(true)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border ${theme.border} ${theme.text} ${theme.cardHover} text-sm font-semibold`}
            >
              <Phone size={14} /> Ask for a time
            </button>
            <button
              onClick={findNext}
              disabled={hunting}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl ${theme.buttonPrimary} text-sm font-semibold disabled:opacity-60`}
            >
              {hunting
                ? <><Loader size={14} className="animate-spin" /> Searching…</>
                : <><Search size={14} /> Find the next {hours}-hour slot</>}
            </button>
            {nextFree && (
              <button
                onClick={() => { const d = new Date(nextFree.start); d.setHours(0, 0, 0, 0); setWeekStart(d); }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border ${theme.border} ${theme.text} text-sm font-medium`}
              >
                <CalendarDays size={14} />
                Next open: {DAY_SHORT(new Date(nextFree.start))}, {DATE_SHORT(new Date(nextFree.start))}
              </button>
            )}
          </div>
        </div>
      ) : (
        /* THE WEEK, AT ONCE. Every cell is a real place a booking of the chosen
           length can start — not an hour you then find you cannot extend. */
        <div className={`${theme.card} rounded-2xl p-4 overflow-x-auto`}>
          <table className="border-separate w-full" style={{ borderSpacing: '4px' }}>
            <thead>
              <tr>
                <th className="w-20" />
                {hourCols.map(h => (
                  <th key={h} className={`text-[11px] font-medium ${theme.textMuted} whitespace-nowrap px-1`}>
                    {new Date(2020, 0, 1, h).toLocaleTimeString(undefined, { hour: 'numeric' })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map(day => {
                const isToday = sameDay(day, today);
                return (
                  <tr key={iso(day)}>
                    <td className="w-20">
                      <div className={`text-left ${isToday ? `${theme.link} font-bold` : theme.text}`}>
                        <div className="text-[11px] opacity-70">{DAY_SHORT(day)}</div>
                        <div className="text-sm font-semibold">{DATE_SHORT(day)}</div>
                      </div>
                    </td>
                    {hourCols.map(h => {
                      const list = cellsByDay.get(`${iso(day)}|${h}`) || [];
                      if (!list.length) {
                        return (
                          <td key={h}>
                            <div className={`h-11 rounded-lg ${theme.backgroundSecondary} opacity-40`} />
                          </td>
                        );
                      }
                      const best = cheapest(list);
                      return (
                        <td key={h}>
                          {/* The price is named rather than left to be read out
                              of the button's text: "$19" and "1 court" sit in
                              adjacent spans with no separator, so textContent
                              reads "$191 court" and any regex over it captures
                              the wrong number. A test that quietly compares 191
                              to 19 is worse than no test. */}
                          <button
                            data-testid="court-slot"
                            onClick={() => setCell({ day, hour: h, options: list })}
                            title={`${list.length} ${list.length === 1 ? 'court' : 'courts'} · from ${money(best.price)}`}
                            className="h-11 w-full rounded-lg bg-green-100 text-green-900 hover:bg-green-600 hover:text-white transition dark:bg-green-900/40 dark:text-green-200 flex flex-col items-center justify-center leading-tight"
                          >
                            <span data-testid="court-slot-price" className="text-[11px] font-bold">{money(best.price)}</span>
                            <span className="text-[9px] opacity-75">
                              {list.length} {list.length === 1 ? 'court' : 'courts'}
                            </span>
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className={`text-[11px] ${theme.textMuted} mt-3`}>
            Each box is a {hours}-hour booking that can start then, on one court, uninterrupted.
          </p>
        </div>
      )}

      {/* Which courts can give you that run */}
      {cell && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4" onClick={() => setCell(null)}>
          <div className={`${theme.card} w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[80vh] overflow-y-auto`}
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className={`text-lg font-bold ${theme.text}`}>
                  {DAY_SHORT(cell.day)}, {DATE_SHORT(cell.day)}
                </h3>
                <p className={`text-sm ${theme.textSecondary}`}>
                  {HOUR_LABEL(cell.options[0].start)} – {HOUR_LABEL(cell.options[0].end)} · {hours} {hours === 1 ? 'hour' : 'hours'}
                </p>
              </div>
              <button onClick={() => setCell(null)} className={`p-1.5 rounded-lg ${theme.cardHover}`}><X size={16} /></button>
            </div>
            {cell.options
              .slice()
              .sort((a, b) => (a.price ?? 0) - (b.price ?? 0))
              .map(r => (
                <div key={r.court.id} className={`flex items-center gap-3 p-3 rounded-xl border ${theme.border}`}>
                  <div className="flex-1 min-w-0">
                    <button onClick={() => setDetail(r.court)} className={`font-semibold ${theme.text} ${theme.linkHover} truncate block`}>
                      {r.court.name}
                    </button>
                    <p className={`text-xs ${theme.textMuted} truncate`}>
                      {(r.court.sports || []).join(' · ') || r.court.type}
                    </p>
                  </div>
                  <span className={`text-sm font-bold ${theme.text} whitespace-nowrap`}>{money(r.price)}</span>
                  <button onClick={() => confirm(r)} disabled={busy}
                    data-testid={`book-court-${r.court.id}`}
                    className={`px-3 py-1.5 ${theme.buttonPrimary} rounded-lg text-xs font-semibold whitespace-nowrap disabled:opacity-60`}>
                    {busy ? 'Holding…' : 'Book'}
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}

      {askOpen && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 sm:p-4"
             onClick={() => setAskOpen(false)}>
          <div className={`${theme.card} w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3`}
               onClick={(e) => e.stopPropagation()}>
            <h3 className={`text-lg font-bold ${theme.text}`}>Ask for a time</h3>
            <p className={`text-sm ${theme.textSecondary}`}>
              Requesting an hour that is not on the grid — and settling it at the
              counter — is coming soon. Until then the academy can book it for you
              directly.
            </p>
            {academy?.phone ? (
              <a href={`tel:${String(academy.phone).replace(/[^+\d]/g, '')}`}
                 className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl ${theme.buttonPrimary} text-sm font-semibold`}>
                <Phone size={15} /> Call {academy.phone}
              </a>
            ) : academy?.email ? (
              <a href={`mailto:${academy.email}`}
                 className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl ${theme.buttonPrimary} text-sm font-semibold`}>
                Email {academy.email}
              </a>
            ) : (
              <p className={`text-sm ${theme.textMuted}`}>
                No contact number is listed for this academy yet.
              </p>
            )}
            <button onClick={() => setAskOpen(false)}
              className={`w-full px-4 py-2 rounded-xl border ${theme.border} ${theme.text} text-sm font-medium`}>
              Close
            </button>
          </div>
        </div>
      )}

      {detail && <CourtDetail court={detail} theme={theme} onClose={() => setDetail(null)} />}
    </div>
  );
};

export default CourtsPublicTab;
